import { z } from "zod";

export const PROPOSAL_INTRO_DRAFT_KEY = "proposal_intro_settings_draft_v1";
export const PROPOSAL_INTRO_PUBLISHED_KEY = "proposal_intro_settings_published_v1";

const imageUrl = z.string().max(2048).nullable().optional();

export const proposalIntroStatCardSchema = z.object({
  value: z.string().min(1).max(64),
  label: z.string().min(1).max(128),
  supportingText: z.string().min(1).max(256),
});

export const proposalIntroServiceCardSchema = z.object({
  imageUrl,
  title: z.string().min(1).max(200),
  description: z.string().min(1).max(4000),
});

export const proposalIntroSettingsSchema = z.object({
  hero: z.object({
    imageUrl,
    headline: z.string().min(1).max(500),
    subheadline: z.string().min(1).max(2000),
  }),
  about: z.object({
    label: z.string().min(1).max(120),
    title: z.string().min(1).max(300),
    body: z.string().min(1).max(8000),
  }),
  statistics: z.array(proposalIntroStatCardSchema).length(4),
  globalExperience: z.object({
    title: z.string().min(1).max(300),
    body: z.string().min(1).max(8000),
  }),
  services: z.array(proposalIntroServiceCardSchema).length(4),
  homeownerGuide: z.object({
    coverImageUrl: imageUrl,
    title: z.string().min(1).max(300),
    description: z.string().min(1).max(4000),
    bullets: z.array(z.string().min(1).max(500)).min(1).max(12),
    pdfUrl: z.string().max(2048).nullable().optional(),
    ctaText: z.string().min(1).max(120),
  }),
});

export type ProposalIntroSettings = z.infer<typeof proposalIntroSettingsSchema>;
export type ProposalIntroStatCard = z.infer<typeof proposalIntroStatCardSchema>;
export type ProposalIntroServiceCard = z.infer<typeof proposalIntroServiceCardSchema>;

export const DEFAULT_PROPOSAL_INTRO_SETTINGS: ProposalIntroSettings = {
  hero: {
    imageUrl: null,
    headline: "For Generations, We Have Been Crafting Extraordinary Entertainment Experiences At Home",
    subheadline:
      "With a heritage dating back to 1929, Clarity has been shaping the way people experience music, film and technology in the most luxurious homes.",
  },
  about: {
    label: "ABOUT CLARITY",
    title: "For Generations, By Generations",
    body: `CLARITY is a Singapore-based family enterprise, controlled by the proprietors for four generations.

From product to services, to giving you assurances and confidence that we will be here to support and take care of you, we think in terms of generations, not just the next quarter.`,
  },
  statistics: [
    { value: "5,000+", label: "Luxury Homes", supportingText: "Around The World" },
    { value: "4", label: "Generations", supportingText: "Family-Owned And Operated" },
    { value: "3", label: "Continents", supportingText: "US, Europe And Asia" },
    { value: "97", label: "Years Of Heritage", supportingText: "Since 1929" },
  ],
  globalExperience: {
    title: "Global Experience. Local Execution.",
    body: `Through projects across North America, Europe and Asia, we continuously study emerging technologies, design trends and best practices from around the world.

Every project benefits from the collective knowledge gained across thousands of homes and decades of experience.`,
  },
  services: [
    {
      imageUrl: null,
      title: "Superlative Audio Systems",
      description:
        "Reference-grade listening environments engineered for effortless performance, refined aesthetics, and the emotional impact of music done properly.",
    },
    {
      imageUrl: null,
      title: "Private Cinemas",
      description:
        "Cinema-grade projection, immersive sound, and calibrated performance — tailored to how you actually watch, entertain, and unwind at home.",
    },
    {
      imageUrl: null,
      title: "Smart Home And Automation",
      description:
        "Reliable whole-home control that feels intuitive day after day — lighting, climate, security, and entertainment working as one seamless experience.",
    },
    {
      imageUrl: null,
      title: "Personal Audio",
      description:
        "Discreet, high-performance personal listening solutions for the spaces where detail, comfort, and privacy matter most.",
    },
  ],
  homeownerGuide: {
    coverImageUrl: null,
    title: "Before You Build, Read This.",
    description:
      "A practical guide for homeowners planning a luxury entertainment or smart home project — what to decide early, what to avoid, and how to protect your investment.",
    bullets: [
      "Plan infrastructure before finishes are locked in",
      "Align technology with how your family actually lives",
      "Budget for integration — not just equipment",
      "Choose partners who will support you for years",
    ],
    pdfUrl: null,
    ctaText: "Download Your Free Guide",
  },
};

export function parseProposalIntroSettings(raw: string | null | undefined): ProposalIntroSettings {
  if (!raw?.trim()) return structuredClone(DEFAULT_PROPOSAL_INTRO_SETTINGS);
  try {
    const parsed = JSON.parse(raw) as unknown;
    const result = proposalIntroSettingsSchema.safeParse(parsed);
    if (!result.success) return structuredClone(DEFAULT_PROPOSAL_INTRO_SETTINGS);
    return result.data;
  } catch {
    return structuredClone(DEFAULT_PROPOSAL_INTRO_SETTINGS);
  }
}

export function mergeProposalIntroSettings(input: unknown): ProposalIntroSettings {
  const base = structuredClone(DEFAULT_PROPOSAL_INTRO_SETTINGS);
  const result = proposalIntroSettingsSchema.safeParse(input);
  if (!result.success) return base;
  return result.data;
}
